"use client"

import JobsSearchBar from "@/app/[locale]/career/jobs/[slug]/components/JobsSearchBar"
import { iconsMap } from "@/public/icons/iconsMap"
import { sendBlockEvent } from "@/utils/gtm"
import styled from "@emotion/styled"
import { Box, List, ListItem, Typography } from "@mui/material"
import Image from "next/image"
import "swiper/css"
import "swiper/css/pagination"
import ButtonWithArrow, {
  colorPalletteIndexType,
} from "ui/common/bit/ButtonWithArrow"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { colors, mediaQueries, sizes } from "ui/theme/theme"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { removeWrappingPTags } from "utils/removeWrappingPTags"
import { extractBlockProperties, transformProperties } from "utils/simplifyData"
import BubbleInfoCard from "../common/BubbleInfoCard"
import CustomBreadcrumbs from "../CustomBreadcrumbs/CustomBreadcrumbs"

const StyledHeroBanner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgcolor" && prop !== "whiteText",
})<{ bgcolor?: string; whiteText?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  width: 100%;
  z-index: 4;
  background-color: ${({ bgcolor }: { bgcolor?: string }) =>
    bgcolor ? `#${bgcolor.trim()}` : "transparent"};
  ${mediaQueries.mobile} {
    flex-direction: column;
    justify-content: flex-end;
    margin-top: unset;
    padding-inline-start: 0;
    padding-inline-end: 0;
  }
  ${mediaQueries.tablet} {
    margin-top: -70px;
    padding-top: 180px;
    flex-direction: column;
    justify-content: flex-end;
  }
  ${mediaQueries.desktopM} {
    width: 100%;
    margin-top: unset;
    height: 580px;
    flex-direction: row;
    padding: 0 60px;
    justify-content: center;
  }
  ${mediaQueries.desktop} {
    height: 680px;
    flex-direction: row;
    padding: 0 60px;
  }
`
const StyledTitlesTextContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "whiteText",
})<{ whiteText: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 635px;
  color: ${({ whiteText }) => (whiteText ? colors.white : colors.black)};
  ${mediaQueries.mobile} {
    max-width: 100%;
    align-items: center;
    text-align: center;
    padding: 134px 40px 0;
  }
  ${mediaQueries.desktopM} {
    flex: 1;
  }
`
const StyledCustomBreadcrumbs = styled(CustomBreadcrumbs)`
  top: 110px;
  ${mediaQueries.mobile} {
    top: 95px;
  }
`
const StyledTitle = styled(Typography)<{ component?: string }>`
  text-align: start;
  color: inherit;
  margin-bottom: ${sizes.baseTriple};
  font-weight: 700;
  ${mediaQueries.mobile} {
    text-align: center;
    max-width: 280px;
  }
  ${mediaQueries.desktopXl} {
    margin-bottom: ${sizes.basePenta};
  }
`
const StyledText = styled(Typography)<{ component?: string }>`
  text-align: start;
  color: inherit;
  margin-bottom: ${sizes.baseTriple};
  font-weight: 400;
  font-size: 24px;
  ${mediaQueries.mobile} {
    text-align: center;
    max-width: 280px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 24px;
    margin-bottom: ${sizes.basePenta};
  }
`
const StyledSubTitle = styled(Typography)<{ component?: string }>`
  text-align: start;
  margin-bottom: 12px;
  color: inherit;
  font-size: 19px;
  ${mediaQueries.mobile} {
    text-align: center;
    max-width: 280px;
  }
  ${mediaQueries.desktopM} {
    font-weight: 400;
    line-height: 23px;
    font-size: 30px;
  }
  ${mediaQueries.desktopXl} {
    font-weight: 400;
    line-height: 27px;
    font-size: 25px;
  }
`

const StyledImageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isWithItemsList",
})<{ isWithItemsList?: boolean }>`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-self: center;
  height: 90%;
  height: 292px;
  margin-bottom: ${(props) => (props.isWithItemsList ? "0" : "14px")};
  ${mediaQueries.desktopM} {
    height: 437px;
    min-width: 350px;
    align-self: flex-end;
    margin-bottom: ${(props) => (props.isWithItemsList ? "0" : "30px")};
  }
  ${mediaQueries.desktop} {
    height: 524px;
  }
`
const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
  object-fit: contain;
`
const StyledButtonsWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${sizes.baseTriple};
  gap: ${sizes.baseDouble};
  ${mediaQueries.mobile} {
    justify-content: center;
  }
  ${mediaQueries.tablet} {
    justify-content: center;
  }
  ${mediaQueries.desktopM} {
    justify-content: flex-start;
  }
`
const StyledListContainer = styled(List, {
  shouldForwardProp: (prop) => prop !== "bgcolor" && prop !== "whiteText",
})<{ bgcolor?: string; whiteText?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  border-top: 1px solid ${colors.gray};
  width: 100%;
  color: ${({ whiteText }) => (whiteText ? colors.white : colors.black)};
  background-color: ${(prop) =>
    prop.bgcolor ? `#${prop.bgcolor?.trim()}` : "transparent"};
  ${mediaQueries.desktopM} {
    padding: 0 40px 40px;
  }
  ${mediaQueries.desktopXl} {
    padding: 0 60px 60px;
  }
`
const StyledLimitWidth = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: ${sizes.maxWidthContainer};
  ${mediaQueries.mobile} {
    flex-flow: row wrap;
    justify-content: flex-start;
    width: 380px;
    padding: 24px 16px 60px;
  }
  ${mediaQueries.tablet} {
    flex-flow: row wrap;
    width: 100%;
    padding: 24px 16px 60px;
  }
  ${mediaQueries.desktopM} {
    width: 1166px;
    flex-flow: row nowrap;
    padding: 40px 0 120px;
    justify-content: space-between;
  }
  ${mediaQueries.desktopXl} {
    width: ${sizes.maxWidthContainer};
  }
`

const StyledListItem = styled(ListItem)`
  color: inherit;
  padding: 0;
  width: fit-content;
  font-size: 12px;
  gap: ${sizes.baseSingle};
  ${mediaQueries.desktopM} {
    font-size: 18px;
  }
  svg {
    display: flex;
    min-width: 28px;
    min-height: 28px;
    flex-shrink: 0;
    ${mediaQueries.desktopM} {
      min-width: 40px;
      min-height: 40px;
    }
  }
`
const StyledListItemText = styled(Typography)<{ component?: string }>`
  font-size: inherit;
  color: inherit;
`

interface HeroBannerProps {
  data: any
  id?: string
  breadcrumbs?: any
}
const HeroBannerMini = ({ data, id, breadcrumbs }: HeroBannerProps) => {
  const {
    backgroundColor,
    image,
    itemsList,
    title,
    subtitle,
    text,
    searchText,
    button,
    additionalButton,
    whiteText,
    bubble,
  } = data
  const checkIconName = colors.getCheckIconKey("#" + backgroundColor?.value) as
    | colorPalletteIndexType
    | "lightTurquoise"
  // Use a fallback icon if getCheckIconKey returns undefined
  const CheckIcon = checkIconName ? iconsMap[checkIconName] : iconsMap["check"]
  const bubbleData = extractBlockProperties(data?.bubble?.blocks)?.[0]
  const handleClick = (buttonText: string) => {
    sendBlockEvent({
      blockId: id || "1",
      title: title?.value,
      componentName: "באנר",
      productName: data?.productName || data?.formDefaultInsuranceType,
      buttonName: buttonText,
    })
  }
  const cleanedTitle = removeWrappingPTags(title?.value || title?.richText)

  return (
    <>
      <StyledHeroBanner
        id={id}
        bgcolor={
          backgroundColor?.value && typeof backgroundColor?.value === "string"
            ? backgroundColor?.value
            : undefined
        }
      >
        {breadcrumbs && (
          <StyledCustomBreadcrumbs
            breadcrumbs={breadcrumbs}
            textColor={whiteText?.value ? colors.white : colors.black}
          />
        )}
        <StyledTitlesTextContainer whiteText={whiteText?.value}>
          {subtitle?.value && (
            <StyledSubTitle variant="h2" component="p">
              {subtitle?.value}
            </StyledSubTitle>
          )}
          {cleanedTitle && (
            <StyledTitle
              variant="h1"
              dangerouslySetInnerHTML={{
                __html: cleanedTitle,
              }}
            />
          )}
          {text?.value && (
            <StyledText variant="h5" component="p">
              {text?.value}
            </StyledText>
          )}
          {searchText?.value && ( // only in Career Jobs Page
            <JobsSearchBar searchText={searchText?.value} />
          )}
          {bubble && bubbleData && <BubbleInfoCard data={bubbleData} />}
          {(button || additionalButton) && (
            <StyledButtonsWrapper>
              {additionalButton && additionalButton?.links[0] && (
                <ButtonWithArrow
                  variant={
                    backgroundColor?.value.toUpperCase() === "141B4D"
                      ? "elevated"
                      : undefined
                  }
                  colorPallette={
                    backgroundColor?.value.toUpperCase() === "141B4D"
                      ? "turquoise"
                      : undefined
                  }
                  href={getNonUmbracoUrl(additionalButton?.links[0]?.url)}
                  onClick={() => handleClick(additionalButton?.links[0]?.name)}
                >
                  {additionalButton.links[0]?.name}
                </ButtonWithArrow>
              )}
              {button && button?.links[0] && (
                <ButtonWithArrow
                  colorPallette="turquoise"
                  href={getNonUmbracoUrl(button?.links[0]?.url)}
                  variant="elevated"
                  onClick={() => handleClick(button?.links[0]?.name)}
                >
                  {button.links[0]?.name}
                </ButtonWithArrow>
              )}
            </StyledButtonsWrapper>
          )}
        </StyledTitlesTextContainer>
        <StyledImageContainer isWithItemsList={!!itemsList}>
          {image?.mediaItems[0] &&
            (isLottieFile(getUmbracoMediaUrl(image.mediaItems[0].url)) ? (
              <LottiePlayer
                lottieUrl={getUmbracoMediaUrl(image.mediaItems[0].url)}
                width={"100%"}
                height={"auto"}
                playOnce={true}
                autoplay={true}
                loop={false}
                ariaLabel={
                  image.mediaItems[0].altText || "hero banner animation"
                }
              />
            ) : (
              <StyledImage
                priority
                width={750}
                height={600}
                src={getUmbracoMediaUrl(image.mediaItems[0].url)}
                style={{ objectFit: "contain", objectPosition: "bottom" }}
                alt={image.mediaItems[0].altText || "hero banner image"}
              />
            ))}
        </StyledImageContainer>
      </StyledHeroBanner>
      {itemsList && (
        <StyledListContainer
          bgcolor={backgroundColor?.value}
          whiteText={whiteText?.value}
        >
          <StyledLimitWidth>
            {itemsList?.blocks &&
              itemsList?.blocks.map((item: any, index: number) => {
                const { title } = transformProperties(item?.properties)
                return (
                  <StyledListItem key={`hero-list-item-${index}`}>
                    <CheckIcon />
                    <StyledListItemText component="h2">
                      {title?.value}
                    </StyledListItemText>
                  </StyledListItem>
                )
              })}
          </StyledLimitWidth>
        </StyledListContainer>
      )}
    </>
  )
}

export default HeroBannerMini
